import { Outlet, createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect } from "react";

import { AppTopbar } from "~/components/app-topbar";
import { CommandPalette } from "~/components/command-palette";
import { CoachPlanPill } from "~/components/nova/coach-plan-pill";
import { SidebarProvider, SidebarInset } from "~/components/ui/sidebar";
import { UserSidebar, useCoachMenu } from "~/components/user-sidebar";
import { myOrganizationsQueryOptions } from "~/lib/functions/cco-representatives";
import { requireAuth } from "~/lib/functions/require-auth";
import { resetIntakeState } from "~/lib/intake/state";
import { coachAccountQueryOptions } from "~/lib/queries/coachAccountQuery";
import { coachStatusQueryOptions } from "~/lib/queries/coachStatusQuery";
import { myCoursesQueryOptions } from "~/lib/queries/learningQuery";
import { myReadinessQueryOptions } from "~/lib/queries/readinessQuery";
import { readSidebarGroupChoices } from "~/lib/sidebar-groups";

/**
 * Home surfaces the U6 first-run intent fork gates on: the member dashboard
 * and the two pages a coach lands on, Welcome and the coach dashboard. The
 * bare `/my-coaching` is left out on purpose: it only redirects to one of
 * those two, so gating it too would run the gate twice. Deliberately narrow —
 * deep links (anything else) pass through untouched and the fork waits for
 * the next plain login to a home surface (plan U6: "only gate on the home
 * surfaces").
 */
const FORK_GATED_PATHS = new Set([
  "/dashboard",
  "/my-coaching/welcome",
  "/my-coaching/clients/dashboard",
]);

export const Route = createFileRoute("/_user")({
  beforeLoad: async ({ location }) => {
    const user = await requireAuth(location);

    // Performance guard: this beforeLoad runs on every `_user` navigation.
    // The fork/resume redirect only ever applies on the home surfaces, so
    // skip the extra round trip everywhere else — including
    // when a `?redirect=` deep link is still attached to the URL (a
    // bookmarked/shared link mid-resolution), which must never be swallowed
    // by the fork.
    const isGatedHome =
      FORK_GATED_PATHS.has(location.pathname) &&
      !location.searchStr.includes("redirect=");

    if (isGatedHome) {
      const [{ consumePostAuthIntentFn }, { getCoachStatus }] =
        await Promise.all([
          import("~/lib/functions/user-intent"),
          import("~/lib/functions/coach-status"),
        ]);

      // consumePostAuthIntentFn is the single source of truth for "does this
      // user have a resolved intent": it persists a still-valid intent
      // cookie (so the gate never races the cookie-consumption write) and
      // lazily backfills pre-launch accounts (R27), returning null only for
      // a genuinely unresolved post-launch account. It also resolves the
      // `dcm_destination` cookie — this gate is the only consumption point
      // for a Clerk OAuth session that lands directly on a bare home surface
      // (a full-page redirect bypasses login's `handleSuccess` entirely), so
      // a cookie-carried deep link (discovery, program invite) must be
      // honored here or it's silently lost.
      const [{ intent, destination }, coachStatus] = await Promise.all([
        consumePostAuthIntentFn(),
        getCoachStatus(),
      ]);

      if (
        destination !== null &&
        destination !== location.pathname + location.searchStr
      ) {
        throw redirect({ to: destination });
      }

      if (intent === null) {
        throw redirect({ to: "/welcome" });
      }

      // Resume-step: coach intent with no coach record yet means the slim
      // registration form is the next step, regardless of which home
      // surface was requested.
      if (intent === "coach" && !coachStatus.isCoach) {
        throw redirect({ to: "/become-a-coach" });
      }
    }

    return {
      user,
    };
  },
  // Primes what the sidebar needs to render the right menu on the server: the
  // coach status the coach menu gates on. When it says this is a non-revoked
  // coach, also prime the course and readiness items the coach menu shows,
  // and the account that says where its Marketing Suite item leads. Without
  // this, a hard load outside `/my-coaching` (e.g. `/messages`) renders the
  // person menu on the server and the client swaps in the coach menu — plus
  // its course items and score badge — right after hydration. The organizations a representative works for are
  // primed for the same reason. Each prefetch catches its own failure so a
  // transient read never fails the layout; a failure just leaves the sidebar
  // to fetch it itself. The menu groups the person collapsed come from the
  // request too, so the server renders them closed.
  loader: async ({ context }) => {
    const [coachStatus] = await Promise.all([
      context.queryClient
        .fetchQuery(coachStatusQueryOptions(context.user.clerkUserId))
        .catch(() => null),
      context.queryClient
        .ensureQueryData(myOrganizationsQueryOptions())
        .catch(() => undefined),
    ]);

    if (coachStatus?.isCoach && !coachStatus.isRevoked) {
      await Promise.all([
        context.queryClient
          .ensureQueryData(myCoursesQueryOptions())
          .catch(() => undefined),
        context.queryClient
          .ensureQueryData(myReadinessQueryOptions())
          .catch(() => undefined),
        // Read after the status: that read links this Clerk user to her coach
        // row, and the account resolves the coach by that link alone.
        context.queryClient
          .ensureQueryData(coachAccountQueryOptions(context.user.clerkUserId))
          .catch(() => undefined),
      ]);
    }

    return { sidebarGroupChoices: await readSidebarGroupChoices() };
  },
  component: UserLayout,
});

function UserLayout() {
  const { sidebarGroupChoices } = Route.useLoaderData();

  // Clear any leftover intake localStorage when entering the authenticated area.
  // The intake route lives outside _user, so this won't affect mid-flow users.
  // Once intake data is saved to DB via account-step, localStorage is redundant.
  useEffect(() => {
    resetIntakeState();
  }, []);

  return (
    <SidebarProvider>
      <UserSidebar groupChoices={sidebarGroupChoices} />
      <CommandPalette />
      <SidebarInset className="[--app-topbar-h:3.5rem]">
        <UserTopbar />
        <div className="flex-1 px-4 py-4 md:px-6 md:py-6">
          <div className="mx-auto max-w-6xl">
            <Outlet />
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}

/**
 * The top bar, whose trail follows the coach menu when she has it. Its own
 * component, so the menu's badge and status reads re-render the bar alone.
 */
function UserTopbar(): React.JSX.Element {
  const coachMenu = useCoachMenu();
  return <AppTopbar menu={coachMenu} status={<CoachPlanPill />} />;
}
